// src/auth/Login.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginAndGetBackendToken } from '../auth/authService';
import { AVISO_LOGIN_KEY } from '../services/api';
import { leerUsuariosRecordados, recordarUsuario, olvidarUsuario } from './usuariosRecordados';

import './Login.css';
import 'bootstrap/dist/css/bootstrap.min.css';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/Finanzas';

  const [username, setUsername] = useState('');
  const [pwd, setPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  // Aviso de sesión vencida/rechazada (lo deja la app al mandar al login); se muestra una sola vez.
  const [err, setErr] = useState(() => { try { const a = sessionStorage.getItem(AVISO_LOGIN_KEY) || ''; sessionStorage.removeItem(AVISO_LOGIN_KEY); return a; } catch { return ''; } });
  const [busy, setBusy] = useState(false);
  // Usuarios recordados en este dispositivo (solo nombres; la contraseña nunca se guarda).
  const [recordados, setRecordados] = useState(() => leerUsuariosRecordados());
  const [listaAbierta, setListaAbierta] = useState(false);
  const usuarioRef = useRef(null);
  const pwdRef = useRef(null);

  // Tocar fuera del campo Usuario o de la lista la cierra.
  useEffect(() => {
    const fuera = (e) => { if (usuarioRef.current && !usuarioRef.current.contains(e.target)) setListaAbierta(false); };
    document.addEventListener('pointerdown', fuera);
    return () => document.removeEventListener('pointerdown', fuera);
  }, []);

  const elegirUsuario = (u) => {
    setUsername(u);
    setPwd('');
    setListaAbierta(false);
    pwdRef.current?.focus();
  };

  const quitarUsuario = (u) => {
    const lista = olvidarUsuario(u);
    setRecordados(lista);
    if (!lista.length) setListaAbierta(false);
  };

  const filtro = username.trim().toLowerCase();
  const sugerencias = recordados.filter((u) => !filtro || u.toLowerCase().includes(filtro));
  const mostrarLista = listaAbierta && !busy && sugerencias.length > 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setErr('');

    if (!username.trim() || !pwd) {
      setErr('Completa todos los campos.');
      return;
    }

    try {
      setBusy(true);
      const data = await loginAndGetBackendToken(username.trim(), pwd);
      recordarUsuario(username.trim()); // solo tras un inicio de sesión exitoso
      navigate(data.mustChangePassword ? '/cambiar-contrasena' : from, { replace: true });
    } catch (e) {
      if (e?.status === 401) setErr(e.message || 'Usuario o contraseña incorrectos.'); // el servidor indica los intentos restantes
      else if (e?.status === 403) setErr(e.message || 'Tu cuenta está deshabilitada. Contacta al administrador.');
      else if (e?.status === 429) setErr(e.message || 'Cuenta bloqueada temporalmente.');
      else if (e instanceof TypeError) setErr('Fallo de red. Revisa tu conexión.');
      else setErr(e?.message || 'Error en el login.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-shell">
      <div className="login-frame">
        <aside className="hero">
          <div className="hero-inner">
            <div className="hero-badge">
              <img src="/img/que-taco.jpg" alt="Que Taco" className="brand-logo"/>
            </div>
            <div className="hero-brand">Que Taco</div>
            <h1 className="hero-title">Sistema de Finanzas</h1>
          </div>
          <div className="hero-pattern" aria-hidden />
        </aside>

        <main className="form-area">
          <div className="form-card">
            <h2 className="form-title">Iniciar sesión</h2>

            {err && <div className="alert alert-danger py-2 small mb-3">{err}</div>}

            <form onSubmit={onSubmit} noValidate>
              {/* Usuario */}
              <div className="mb-3">
                <label className="form-label" htmlFor="usernameInput">Usuario</label>
                <div className="input-with-icon usuario-combo" ref={usuarioRef}>
                  <span className="icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--brand-dark)">
                      <path d="M12 12c2.76 0 5-2.69 5-6s-2.24-6-5-6-5 2.69-5 6 2.24 6 5 6Zm0 2c-4.42 0-8 3.13-8 7v1h16v-1c0-3.87-3.58-7-8-7Z"/>
                    </svg>
                  </span>
                  <input
                    id="usernameInput"
                    type="text"
                    className="form-control"
                    placeholder="Usuario"
                    value={username}
                    onChange={(e) => { setUsername(e.target.value); setListaAbierta(true); }}
                    onClick={() => setListaAbierta(true)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setListaAbierta(false); }}
                    autoComplete="off"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    role="combobox"
                    aria-controls="usuariosRecordadosLista"
                    aria-expanded={mostrarLista ? 'true' : 'false'}
                    aria-autocomplete="list"
                    required
                    disabled={busy}
                  />
                  {recordados.length > 0 && (
                    <button
                      type="button"
                      className="toggle-pass"
                      aria-label="Mostrar usuarios recordados"
                      title="Usuarios recordados"
                      onPointerDown={(e) => e.preventDefault()}
                      onClick={() => setListaAbierta((a) => !a)}
                      disabled={busy}
                    >▾</button>
                  )}
                  {mostrarLista && (
                    <ul id="usuariosRecordadosLista" className="usuarios-recordados" role="listbox">
                      {sugerencias.map((u) => (
                        <li key={u} role="option" aria-selected={u === username}>
                          <button type="button" className="ur-elegir"
                            onPointerDown={(e) => e.preventDefault()} onClick={() => elegirUsuario(u)}>{u}</button>
                          <button type="button" className="ur-quitar"
                            aria-label={`Quitar ${u} de este dispositivo`} title="Quitar de este dispositivo"
                            onPointerDown={(e) => e.preventDefault()} onClick={() => quitarUsuario(u)}>×</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              {/* Password */}
              <div className="mb-2">
                <label className="form-label" htmlFor="password">Contraseña</label>
                <div className="input-with-icon">
                  <span className="icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--brand-dark)">
                      <path d="M17 9V7a5 5 0 0 0-10 0v2H5v13h14V9h-2Zm-8 0V7a3 3 0 0 1 6 0v2H9Zm3 5a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/>
                    </svg>
                  </span>
                  <input
                    id="password"
                    ref={pwdRef}
                    type={showPwd ? 'text' : 'password'}
                    className="form-control"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    autoComplete="new-password"
                    required
                    disabled={busy}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="toggle-pass"
                    onClick={() => setShowPwd(s => !s)}
                    aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    title={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                    disabled={busy}
                  >
                    {showPwd ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--brand-dark)">
                        <path d="M2 2l20 20-1.5 1.5L18 19.5c-1.8 1-3.8 1.5-6 1.5-5.5 0-10-3.5-12-9 1-2.7 2.7-4.9 4.8-6.5L.5 3.5 2 2Zm5.7 5.7C9.3 7 10.6 6.5 12 6.5c4.1 0 7.5 2.4 9.2 6-1 2.3-2.6 4-4.6 5.1l-2.3-2.3A4.5 4.5 0 0 0 9 9l-1.3-1.3Z"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="var(--brand-dark)">
                        <path d="M12 5c-5 0-9.3 3-11 7 1.7 4 6 7 11 7s9.3-3 11-7c-1.7-4-6-7-11-7Zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm0-2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Sin recuperación de contraseña: la restablece un administrador en Usuarios */}
              <div className="remember-row" />

              <button className="btn btn-primary w-100" disabled={busy}>
                {busy ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Ingresando…
                  </>
                ) : 'Iniciar sesión'}
              </button>
            </form>
          </div>
          <footer className="login-attribution">
            <img src="/img/sistemi-logo.png" alt="Logo de SISTEMI" />
            <div><strong>Desarrollado por SISTEMI</strong>
              <small>© SISTEMI. Todos los derechos reservados.</small></div>
          </footer>
        </main>
      </div>
    </div>
  );
}
